import { Box, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { parse } from '@finley-technologies/valley-bank-utils';
import type { ValleyBankCustomerWithRelationships } from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankCustomerQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { ProgressAlert } from 'packages/common-frontend/components/Alert';
import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useCustomerUrlParam } from 'valleyBank/domains/Customers/hooks/CustomerContext';
import { CustomerDetailsTemplate } from 'valleyBank/domains/Customers/ui/details/CustomerDetailsTemplate';
import { CustomerInfo } from 'valleyBank/domains/Customers/ui/details/CustomerInfo';
import { CustomerNotFoundAlert } from 'valleyBank/domains/Customers/ui/details/CustomerNotFoundAlert';
import { useDataMetadataContext } from 'valleyBank/domains/page/hooks/DataMetadataContext';

import 'source/borrower/borrower.scss';

export const CustomersDetailsCustomerInfoRoute = () => {
  const toast = useToast();
  const history = useHistory();
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();

  const { cifnum } = useCustomerUrlParam();

  const [
    customer,
    setCustomer,
  ] = useState<ValleyBankCustomerWithRelationships | null>(null);

  const { loading: isDataLoading } = useGetValleyBankCustomerQuery({
    variables: {
      cifNumber: cifnum || '',
      reportingDate,
      isPreview,
    },
    onCompleted: (res) => {
      const customerJSON = res.valleyBankCustomer?.customer;
      if (customerJSON) {
        try {
          const newCustomer = parse.parseValleyBankCustomer(customerJSON);
          if (newCustomer) {
            setAsOfDate(newCustomer.asOfDate);
            setCustomer({ ...newCustomer });
          }
        } catch (err) {
          Sentry.captureException(err);
          throw new Error('Failed to parse customer');
        }
      }
    },
  });

  let routeLevelError = false;
  return (
    <Box padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load settings page`;
            routeLevelError = true;
            toast(errorToastConfig);
            history.push('/');
          }
        }}
      >
        <CustomerDetailsTemplate selectedTab="info">
          {isDataLoading && (
            <ProgressAlert status="info">
              <Text>{`Loading holdings for customer CIF Number [${cifnum}]`}</Text>
            </ProgressAlert>
          )}
          {!isDataLoading && !customer && <CustomerNotFoundAlert />}

          {customer && <CustomerInfo customer={customer} />}
        </CustomerDetailsTemplate>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
